// skills/bounty/bountyData.js
// Bounty contract definitions and configuration

import gameState from '../../game/gameState.js';

/**
 * Contract Types:
 * - gathering: Collect raw resources (ores, logs, fish)
 * - processing: Create processed items (bars, planks, cooked food)
 * - kill: Combat-based contracts (kill specific mobs)
 */

export const PLAYER_CONTRACTS = {
    // ============ Tier 1 - Level 1+ Bounty ============
    // Gathering contracts
    GATHER_PYRESTONE: {
        id: 'gather_pyrestone',
        name: 'Pyrestone Collection',
        tier: 1,
        type: 'gathering',
        description: 'The smiths need pyrestone ore for their forges.',
        requirements: [{ itemId: 'pyrestone_ore', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 100, xp: { mining: 50 } }
    },
    GATHER_PALE_SHARD: {
        id: 'gather_pale_shard',
        name: 'Pale Shard Procurement',
        tier: 1,
        type: 'gathering',
        description: 'Pale shard ore is needed for alloys.',
        requirements: [{ itemId: 'pale_shard_ore', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 100, xp: { mining: 50 } }
    },
    GATHER_TIMBER: {
        id: 'gather_timber',
        name: 'Common Timber',
        tier: 1,
        type: 'gathering',
        description: 'The carpenters require sturdy timber.',
        requirements: [{ itemId: 'common_timber', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 100, xp: { woodcutting: 50 } }
    },
    GATHER_SAND_SMELT: {
        id: 'gather_sand_smelt',
        name: 'Sand Smelt Haul',
        tier: 1,
        type: 'gathering',
        description: 'The tavern needs fresh sand smelt for tonight\'s stew.',
        requirements: [{ itemId: 'raw_sand_smelt', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 80, xp: { fishing: 40 } }
    },

    // Processing contracts
    SMELT_PYRESTONE: {
        id: 'smelt_pyrestone',
        name: 'Pyrestone Bars',
        tier: 1,
        type: 'processing',
        description: 'Smelt pyrestone bars for the armory.',
        requirements: [{ itemId: 'pyrestone_bar', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 150, xp: { smithing: 75 } }
    },
    COOK_SAND_SMELT: {
        id: 'cook_sand_smelt',
        name: 'Cooked Sand Smelt',
        tier: 1,
        type: 'processing',
        description: 'Prepare cooked sand smelt for the inn.',
        requirements: [{ itemId: 'cooked_sand_smelt', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 120, xp: { cooking: 60 } }
    },

    // Crafting contracts
    CRAFT_SAPPHIRE_BAND: {
        id: 'craft_sapphire_band',
        name: 'Sapphire Rings',
        tier: 1,
        type: 'processing',
        description: 'The jeweler needs sapphire bands for a wedding order.',
        requirements: [{ itemId: 'sapphire_band', targetMin: 5, targetMax: 15 }],
        baseRewards: { gold: 180, xp: { crafting: 90 } }
    },

    // Glyphbinding contracts
    GATHER_ARCANE_RESIDUE: {
        id: 'gather_arcane_residue',
        name: 'Arcane Extraction',
        tier: 1,
        type: 'gathering',
        description: 'The mages need raw arcane residue for experiments.',
        requirements: [{ itemId: 'arcane_residue', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 120, xp: { glyphbinding: 60 } }
    },
    CRAFT_STORM_GLYPH: {
        id: 'craft_storm_glyph',
        name: 'Storm Glyph Order',
        tier: 1,
        type: 'processing',
        description: 'Enchanters require storm glyphs for wind magic.',
        requirements: [{ itemId: 'storm_glyph', targetMin: 10, targetMax: 30 }],
        baseRewards: { gold: 150, xp: { glyphbinding: 75 } }
    },

    // Gatherer contracts
    GATHER_BASIC_PET_FOOD: {
        id: 'gather_basic_pet_food',
        name: 'Pet Food Supplies',
        tier: 1,
        type: 'gathering',
        description: 'The stables need basic pet food for the animals.',
        requirements: [{ itemId: 'basic_pet_food', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 100, xp: { gatherer: 50 } }
    },

    // Combat contracts
    KILL_GOBLIN_RAIDERS: {
        id: 'kill_goblin_raiders',
        name: 'Goblin Raider Hunt',
        tier: 1,
        type: 'kill',
        description: 'Clear the goblin raiders from the camp near the village.',
        requirements: [{ mobId: 'goblin_raider', targetMin: 15, targetMax: 35 }],
        baseRewards: { gold: 200, xp: { combat: 100 } }
    },
    KILL_SCAVENGERS: {
        id: 'kill_scavengers',
        name: 'Scavenger Cleanup',
        tier: 1,
        type: 'kill',
        description: 'Drive out the scavengers from the forgotten outskirts.',
        requirements: [{ mobId: 'scavenger', targetMin: 20, targetMax: 45 }],
        baseRewards: { gold: 250, xp: { combat: 125 } }
    },

    // ============ Tier 2 - Level 15+ Bounty ============
    GATHER_BLACKIRON: {
        id: 'gather_blackiron',
        name: 'Blackiron Rush',
        tier: 2,
        type: 'gathering',
        description: 'The kingdom\'s forges demand blackiron ore.',
        requirements: [{ itemId: 'blackiron_ore', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 350, xp: { mining: 150 } }
    },
    GATHER_IRONWOOD: {
        id: 'gather_ironwood',
        name: 'Ironwood Harvest',
        tier: 2,
        type: 'gathering',
        description: 'Fletchers need ironwood for arrow shafts.',
        requirements: [{ itemId: 'ironwood_logs', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 350, xp: { woodcutting: 150 } }
    },
    GATHER_RIVER_CHAR: {
        id: 'gather_river_char',
        name: 'River Char Run',
        tier: 2,
        type: 'gathering',
        description: 'The noble feast requires fresh river char.',
        requirements: [{ itemId: 'raw_river_char', targetMin: 40, targetMax: 110 }],
        baseRewards: { gold: 400, xp: { fishing: 175 } }
    },
    SMELT_PALE_SHARD: {
        id: 'smelt_pale_shard',
        name: 'Pale Shard Smithing',
        tier: 2,
        type: 'processing',
        description: 'The armory needs pale shard bars urgently.',
        requirements: [{ itemId: 'pale_shard_bar', targetMin: 40, targetMax: 110 }],
        baseRewards: { gold: 500, xp: { smithing: 200 } }
    },
    COOK_RIVER_CHAR: {
        id: 'cook_river_char',
        name: 'River Char Feast',
        tier: 2,
        type: 'processing',
        description: 'Prepare river char for the royal banquet.',
        requirements: [{ itemId: 'cooked_river_char', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 450, xp: { cooking: 180 } }
    },

    // Crafting contracts - Tier 2
    CRAFT_EMERALD_BAND: {
        id: 'craft_emerald_band',
        name: 'Emerald Jewelry',
        tier: 2,
        type: 'processing',
        description: 'Noble families desire emerald bands.',
        requirements: [{ itemId: 'emerald_band', targetMin: 10, targetMax: 30 }],
        baseRewards: { gold: 500, xp: { crafting: 200 } }
    },
    CRAFT_SAPPHIRE_PENDANT: {
        id: 'craft_sapphire_pendant',
        name: 'Sapphire Pendants',
        tier: 2,
        type: 'processing',
        description: 'The merchant guild ordered sapphire pendants.',
        requirements: [{ itemId: 'sapphire_pendant', targetMin: 8, targetMax: 24 }],
        baseRewards: { gold: 450, xp: { crafting: 180 } }
    },

    // Glyphbinding contracts - Tier 2
    GATHER_REFINED_ICHOR: {
        id: 'gather_refined_ichor',
        name: 'Refined Ichor Collection',
        tier: 2,
        type: 'gathering',
        description: 'The academy requires refined arcane ichor.',
        requirements: [{ itemId: 'refined_arcane_ichor', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 400, xp: { glyphbinding: 175 } }
    },
    CRAFT_FROST_GLYPH: {
        id: 'craft_frost_glyph',
        name: 'Frost Glyph Commission',
        tier: 2,
        type: 'processing',
        description: 'Frost glyphs are needed for preservation enchantments.',
        requirements: [{ itemId: 'frost_glyph', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 450, xp: { glyphbinding: 200 } }
    },
    CRAFT_FLAME_GLYPH: {
        id: 'craft_flame_glyph',
        name: 'Flame Glyph Requisition',
        tier: 2,
        type: 'processing',
        description: 'The forge masters need flame glyphs.',
        requirements: [{ itemId: 'flame_glyph', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 450, xp: { glyphbinding: 200 } }
    },

    // Gatherer contracts - Tier 2
    GATHER_REFINED_PET_FOOD: {
        id: 'gather_refined_pet_food',
        name: 'Premium Pet Food',
        tier: 2,
        type: 'gathering',
        description: 'The royal menagerie requires refined pet food.',
        requirements: [{ itemId: 'refined_pet_food', targetMin: 60, targetMax: 180 }],
        baseRewards: { gold: 350, xp: { gatherer: 150 } }
    },

    // Smithing - Arrowheads
    SMITH_PYRESTONE_ARROWHEADS: {
        id: 'smith_pyrestone_arrowheads',
        name: 'Arrowhead Forging',
        tier: 2,
        type: 'processing',
        description: 'The army needs pyrestone arrowheads.',
        requirements: [{ itemId: 'pyrestone_arrowheads', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 400, xp: { smithing: 175 } }
    },

    // Combat contracts
    KILL_FERAL_BEASTS: {
        id: 'kill_feral_beasts',
        name: 'Feral Beast Culling',
        tier: 2,
        type: 'kill',
        description: 'Feral beasts are threatening the livestock.',
        requirements: [{ mobId: 'feral_beast', targetMin: 25, targetMax: 55 }],
        baseRewards: { gold: 600, xp: { combat: 300 } }
    },
    KILL_BANDIT_THUGS: {
        id: 'kill_bandit_thugs',
        name: 'Bandit Thug Bounty',
        tier: 2,
        type: 'kill',
        description: 'The bandit thugs are robbing travelers on the road.',
        requirements: [{ mobId: 'bandit_thug', targetMin: 20, targetMax: 45 }],
        baseRewards: { gold: 700, xp: { combat: 350 } }
    },

    // ============ Tier 3 - Level 30+ Bounty ============
    GATHER_DEEPCOAL: {
        id: 'gather_deepcoal',
        name: 'Deepcoal Expedition',
        tier: 3,
        type: 'gathering',
        description: 'Fuel is needed for the industrial furnaces.',
        requirements: [{ itemId: 'deepcoal', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 800, xp: { mining: 400 } }
    },
    GATHER_SOFTBARK: {
        id: 'gather_softbark',
        name: 'Softbark Logging',
        tier: 3,
        type: 'gathering',
        description: 'Softbark wood is prized for fine furniture.',
        requirements: [{ itemId: 'softbark_logs', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 800, xp: { woodcutting: 400 } }
    },
    GATHER_CRIMSON_CHAR: {
        id: 'gather_crimson_char',
        name: 'Crimson Char Dive',
        tier: 3,
        type: 'gathering',
        description: 'Rare crimson char fetch high prices.',
        requirements: [{ itemId: 'raw_crimson_char', targetMin: 75, targetMax: 225 }],
        baseRewards: { gold: 1200, xp: { fishing: 600 } }
    },
    SMELT_BLACKIRON: {
        id: 'smelt_blackiron',
        name: 'Blackiron Production',
        tier: 3,
        type: 'processing',
        description: 'The military needs blackiron for weapons.',
        requirements: [{ itemId: 'blackiron_bar', targetMin: 75, targetMax: 225 }],
        baseRewards: { gold: 1500, xp: { smithing: 750 } }
    },

    // Crafting contracts - Tier 3
    CRAFT_RUBY_BAND: {
        id: 'craft_ruby_band',
        name: 'Ruby Treasures',
        tier: 3,
        type: 'processing',
        description: 'The crown jeweler requires ruby bands.',
        requirements: [{ itemId: 'ruby_band', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 1200, xp: { crafting: 600 } }
    },
    CRAFT_EMERALD_PENDANT: {
        id: 'craft_emerald_pendant',
        name: 'Emerald Pendants',
        tier: 3,
        type: 'processing',
        description: 'Emerald pendants for the nobility.',
        requirements: [{ itemId: 'emerald_pendant', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 1000, xp: { crafting: 500 } }
    },

    // Glyphbinding contracts - Tier 3
    GATHER_PURIFIED_CORE: {
        id: 'gather_purified_core',
        name: 'Purified Core Harvest',
        tier: 3,
        type: 'gathering',
        description: 'The archmage needs purified arcane cores.',
        requirements: [{ itemId: 'purified_arcane_core', targetMin: 75, targetMax: 225 }],
        baseRewards: { gold: 1000, xp: { glyphbinding: 500 } }
    },
    CRAFT_VIGOR_GLYPH: {
        id: 'craft_vigor_glyph',
        name: 'Vigor Glyph Creation',
        tier: 3,
        type: 'processing',
        description: 'Vigor glyphs are needed for fortification rituals.',
        requirements: [{ itemId: 'vigor_glyph', targetMin: 35, targetMax: 105 }],
        baseRewards: { gold: 1100, xp: { glyphbinding: 550 } }
    },
    CRAFT_STONE_GLYPH: {
        id: 'craft_stone_glyph',
        name: 'Stone Glyph Production',
        tier: 3,
        type: 'processing',
        description: 'Stone glyphs for fortress enchantments.',
        requirements: [{ itemId: 'stone_glyph', targetMin: 35, targetMax: 105 }],
        baseRewards: { gold: 1100, xp: { glyphbinding: 550 } }
    },

    // Gatherer contracts - Tier 3
    GATHER_CONDENSED_PET_FOOD: {
        id: 'gather_condensed_pet_food',
        name: 'Elite Pet Food',
        tier: 3,
        type: 'gathering',
        description: 'The beast tamers need condensed pet food.',
        requirements: [{ itemId: 'condensed_pet_food', targetMin: 80, targetMax: 240 }],
        baseRewards: { gold: 800, xp: { gatherer: 400 } }
    },

    // Smithing - Arrowheads
    SMITH_BLACKIRON_ARROWHEADS: {
        id: 'smith_blackiron_arrowheads',
        name: 'Blackiron Arrows',
        tier: 3,
        type: 'processing',
        description: 'Elite archers need blackiron arrowheads.',
        requirements: [{ itemId: 'blackiron_arrowheads', targetMin: 150, targetMax: 450 }],
        baseRewards: { gold: 1000, xp: { smithing: 500 } }
    },

    // Combat contracts
    KILL_BANDIT_ENFORCERS: {
        id: 'kill_bandit_enforcers',
        name: 'Bandit Enforcer Purge',
        tier: 3,
        type: 'kill',
        description: 'The trade routes are plagued by bandit enforcers.',
        requirements: [{ mobId: 'bandit_enforcer', targetMin: 35, targetMax: 85 }],
        baseRewards: { gold: 2000, xp: { combat: 1000 } }
    },
    KILL_CAVE_TROLLS: {
        id: 'kill_cave_trolls',
        name: 'Cave Troll Extermination',
        tier: 3,
        type: 'kill',
        description: 'Cave trolls are blocking the mining tunnels.',
        requirements: [{ mobId: 'cave_troll', targetMin: 30, targetMax: 70 }],
        baseRewards: { gold: 2200, xp: { combat: 1100 } }
    },
    KILL_SKELETON_WARRIORS: {
        id: 'kill_skeleton_warriors',
        name: 'Undead Purification',
        tier: 3,
        type: 'kill',
        description: 'The crypts have become overrun with skeleton warriors.',
        requirements: [{ mobId: 'skeleton_warrior', targetMin: 30, targetMax: 75 }],
        baseRewards: { gold: 2400, xp: { combat: 1200 } }
    },

    // ============ Tier 4 - Level 50+ Bounty ============
    GATHER_SKYIRON: {
        id: 'gather_skyiron',
        name: 'Skyiron Mining',
        tier: 4,
        type: 'gathering',
        description: 'The rare skyiron ore is highly valued.',
        requirements: [{ itemId: 'skyiron_ore', targetMin: 200, targetMax: 600 }],
        baseRewards: { gold: 3000, xp: { mining: 1500 } }
    },
    GATHER_GRAVEWOOD: {
        id: 'gather_gravewood',
        name: 'Gravewood Collection',
        tier: 4,
        type: 'gathering',
        description: 'Gravewood makes the finest bows.',
        requirements: [{ itemId: 'gravewood_logs', targetMin: 200, targetMax: 600 }],
        baseRewards: { gold: 3000, xp: { woodcutting: 1500 } }
    },
    GATHER_SPINY_LOBSTER: {
        id: 'gather_spiny_lobster',
        name: 'Spiny Lobster Hunt',
        tier: 4,
        type: 'gathering',
        description: 'Expert anglers are needed for spiny lobsters.',
        requirements: [{ itemId: 'raw_spiny_lobster', targetMin: 150, targetMax: 450 }],
        baseRewards: { gold: 4000, xp: { fishing: 2000 } }
    },
    SMELT_SKYIRON: {
        id: 'smelt_skyiron',
        name: 'Skyiron Forging',
        tier: 4,
        type: 'processing',
        description: 'Forge skyiron bars for elite equipment.',
        requirements: [{ itemId: 'skyiron_bar', targetMin: 125, targetMax: 375 }],
        baseRewards: { gold: 6000, xp: { smithing: 3000 } }
    },

    // Crafting contracts - Tier 4
    CRAFT_DIAMOND_BAND: {
        id: 'craft_diamond_band',
        name: 'Diamond Rings',
        tier: 4,
        type: 'processing',
        description: 'The royal treasury demands diamond bands.',
        requirements: [{ itemId: 'diamond_band', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 3500, xp: { crafting: 1750 } }
    },
    CRAFT_RUBY_PENDANT: {
        id: 'craft_ruby_pendant',
        name: 'Ruby Pendants',
        tier: 4,
        type: 'processing',
        description: 'Ruby pendants for the autumn festival.',
        requirements: [{ itemId: 'ruby_pendant', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 3000, xp: { crafting: 1500 } }
    },

    // Glyphbinding contracts - Tier 4
    CRAFT_VERDANT_GLYPH: {
        id: 'craft_verdant_glyph',
        name: 'Verdant Glyph Enchanting',
        tier: 4,
        type: 'processing',
        description: 'Druids require verdant glyphs for grove rituals.',
        requirements: [{ itemId: 'verdant_glyph', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 3200, xp: { glyphbinding: 1600 } }
    },
    CRAFT_ENTROPY_GLYPH: {
        id: 'craft_entropy_glyph',
        name: 'Entropy Glyph Creation',
        tier: 4,
        type: 'processing',
        description: 'The warlock council needs entropy glyphs.',
        requirements: [{ itemId: 'entropy_glyph', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 3500, xp: { glyphbinding: 1750 } }
    },
    CRAFT_VOID_GLYPH: {
        id: 'craft_void_glyph',
        name: 'Void Glyph Requisition',
        tier: 4,
        type: 'processing',
        description: 'The observatory requires void glyphs for Veil research.',
        requirements: [{ itemId: 'void_glyph', targetMin: 45, targetMax: 135 }],
        baseRewards: { gold: 3800, xp: { glyphbinding: 1900 } }
    },

    // Gatherer contracts - Tier 4 (Awakening materials)
    GATHER_LIVING_HUSK: {
        id: 'gather_living_husk',
        name: 'Arcane Husk Hunt',
        tier: 4,
        type: 'gathering',
        description: 'Pet tamers need living arcane husks for awakening.',
        requirements: [{ itemId: 'living_arcane_husk', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 3500, xp: { gatherer: 1750 } }
    },

    // Smithing arrowheads - Tier 4
    SMITH_SKYIRON_ARROWHEADS: {
        id: 'smith_skyiron_arrowheads',
        name: 'Skyiron Arrow Tips',
        tier: 4,
        type: 'processing',
        description: 'Elite rangers need skyiron arrowheads.',
        requirements: [{ itemId: 'skyiron_arrowheads', targetMin: 200, targetMax: 600 }],
        baseRewards: { gold: 3000, xp: { smithing: 1500 } }
    },

    // Combat contracts
    KILL_TROLL_BRUTES: {
        id: 'kill_troll_brutes',
        name: 'Troll Brute Slaying',
        tier: 4,
        type: 'kill',
        description: 'Massive troll brutes threaten the mines.',
        requirements: [{ mobId: 'troll_brute', targetMin: 40, targetMax: 100 }],
        baseRewards: { gold: 8000, xp: { combat: 4000 } }
    },
    KILL_LESSER_DEMONS: {
        id: 'kill_lesser_demons',
        name: 'Demon Containment',
        tier: 4,
        type: 'kill',
        description: 'Lesser demons are escaping from the Demon Scar.',
        requirements: [{ mobId: 'lesser_demon', targetMin: 35, targetMax: 90 }],
        baseRewards: { gold: 9000, xp: { combat: 4500 } }
    },
    KILL_SCARRED_CULTISTS: {
        id: 'kill_scarred_cultists',
        name: 'Cult Disruption',
        tier: 4,
        type: 'kill',
        description: 'Scarred cultists are performing dark rituals.',
        requirements: [{ mobId: 'scarred_cultist', targetMin: 40, targetMax: 95 }],
        baseRewards: { gold: 7500, xp: { combat: 3750 } }
    },

    // ============ Tier 5 - Level 70+ Bounty ============
    GATHER_VOIDSTEEL: {
        id: 'gather_voidsteel',
        name: 'Voidsteel Expedition',
        tier: 5,
        type: 'gathering',
        description: 'The legendary voidsteel ore awaits.',
        requirements: [{ itemId: 'voidsteel_ore', targetMin: 500, targetMax: 1500 }],
        baseRewards: { gold: 15000, xp: { mining: 7500 } }
    },
    GATHER_ARCANEWOOD: {
        id: 'gather_arcanewood',
        name: 'Arcanewood Timber',
        tier: 5,
        type: 'gathering',
        description: 'Enchanted arcanewood logs are required.',
        requirements: [{ itemId: 'arcanewood_logs', targetMin: 500, targetMax: 1500 }],
        baseRewards: { gold: 15000, xp: { woodcutting: 7500 } }
    },
    GATHER_MAKO: {
        id: 'gather_mako',
        name: 'Mako Hunting',
        tier: 5,
        type: 'gathering',
        description: 'Only the bravest fish for mako.',
        requirements: [{ itemId: 'raw_mako', targetMin: 375, targetMax: 1125 }],
        baseRewards: { gold: 20000, xp: { fishing: 10000 } }
    },
    SMELT_VOIDSTEEL: {
        id: 'smelt_voidsteel',
        name: 'Voidsteel Mastery',
        tier: 5,
        type: 'processing',
        description: 'Master smiths are needed for voidsteel.',
        requirements: [{ itemId: 'voidsteel_bar', targetMin: 250, targetMax: 750 }],
        baseRewards: { gold: 25000, xp: { smithing: 12500 } }
    },

    // Crafting contracts - Tier 5
    CRAFT_ASTRYX_BAND: {
        id: 'craft_astryx_band',
        name: 'Astryx Stone Rings',
        tier: 5,
        type: 'processing',
        description: 'The archmage council demands astryx stone bands.',
        requirements: [{ itemId: 'astryx_stone_band', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 12000, xp: { crafting: 6000 } }
    },
    CRAFT_ONYX_BAND: {
        id: 'craft_onyx_band',
        name: 'Onyx Band Mastery',
        tier: 5,
        type: 'processing',
        description: 'The shadow guild requests onyx bands.',
        requirements: [{ itemId: 'onyx_band', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 15000, xp: { crafting: 7500 } }
    },
    CRAFT_ASTRYX_PENDANT: {
        id: 'craft_astryx_pendant',
        name: 'Astryx Pendants',
        tier: 5,
        type: 'processing',
        description: 'Astryx stone pendants for the celestial ceremony.',
        requirements: [{ itemId: 'astryx_stone_pendant', targetMin: 45, targetMax: 135 }],
        baseRewards: { gold: 13000, xp: { crafting: 6500 } }
    },
    CRAFT_ONYX_PENDANT: {
        id: 'craft_onyx_pendant',
        name: 'Onyx Pendant Mastery',
        tier: 5,
        type: 'processing',
        description: 'Master jewelers needed for onyx pendants.',
        requirements: [{ itemId: 'onyx_pendant', targetMin: 35, targetMax: 105 }],
        baseRewards: { gold: 18000, xp: { crafting: 9000 } }
    },

    // Glyphbinding contracts - Tier 5
    CRAFT_DOOM_GLYPH: {
        id: 'craft_doom_glyph',
        name: 'Doom Glyph Inscription',
        tier: 5,
        type: 'processing',
        description: 'Necromancers require doom glyphs for dark rituals.',
        requirements: [{ itemId: 'doom_glyph', targetMin: 60, targetMax: 180 }],
        baseRewards: { gold: 14000, xp: { glyphbinding: 7000 } }
    },
    CRAFT_SANGUINE_GLYPH: {
        id: 'craft_sanguine_glyph',
        name: 'Sanguine Glyph Forging',
        tier: 5,
        type: 'processing',
        description: 'Blood mages demand sanguine glyphs of power.',
        requirements: [{ itemId: 'sanguine_glyph', targetMin: 55, targetMax: 165 }],
        baseRewards: { gold: 16000, xp: { glyphbinding: 8000 } }
    },
    CRAFT_SPIRIT_GLYPH: {
        id: 'craft_spirit_glyph',
        name: 'Spirit Glyph Binding',
        tier: 5,
        type: 'processing',
        description: 'Those who commune with the Veil require spirit glyphs.',
        requirements: [{ itemId: 'spirit_glyph', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 20000, xp: { glyphbinding: 10000 } }
    },

    // Gatherer contracts - Tier 5 (Awakening materials)
    GATHER_ARCANE_CORE: {
        id: 'gather_arcane_core',
        name: 'Arcane Core Extraction',
        tier: 5,
        type: 'gathering',
        description: 'Pet masters need condensed arcane cores for ultimate awakening.',
        requirements: [{ itemId: 'condensed_arcane_core_fragment', targetMin: 10, targetMax: 30 }],
        baseRewards: { gold: 20000, xp: { gatherer: 10000 } }
    },

    // Smithing arrowheads - Tier 5
    SMITH_VOIDSTEEL_ARROWHEADS: {
        id: 'smith_voidsteel_arrowheads',
        name: 'Voidsteel Arrow Tips',
        tier: 5,
        type: 'processing',
        description: 'Legendary archers demand voidsteel arrowheads.',
        requirements: [{ itemId: 'voidsteel_arrowheads', targetMin: 300, targetMax: 900 }],
        baseRewards: { gold: 15000, xp: { smithing: 7500 } }
    },

    // Combat contracts
    KILL_ANCIENT_DRAGONS: {
        id: 'kill_ancient_dragons',
        name: 'Dragon Slayer',
        tier: 5,
        type: 'kill',
        description: 'Slay the ancient dragons terrorizing the realm.',
        requirements: [{ mobId: 'ancient_dragon', targetMin: 15, targetMax: 35 }],
        baseRewards: { gold: 50000, xp: { combat: 25000 } }
    },
    KILL_RIFT_HORRORS: {
        id: 'kill_rift_horrors',
        name: 'Void Rift Cleansing',
        tier: 5,
        type: 'kill',
        description: 'Destroy the rift horrors emerging from the void.',
        requirements: [{ mobId: 'rift_horror', targetMin: 12, targetMax: 30 }],
        baseRewards: { gold: 55000, xp: { combat: 27500 } }
    },
    KILL_ELDER_CONSTRUCTS: {
        id: 'kill_elder_constructs',
        name: 'Sanctum Siege',
        tier: 5,
        type: 'kill',
        description: 'Destroy the elder constructs guarding the Ancient Sanctum.',
        requirements: [{ mobId: 'elder_construct', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 60000, xp: { combat: 30000 } }
    },

    // ==================== NEW TIER 1 CONTRACTS ====================

    // Fish
    GATHER_SILVER_SPRAT: {
        id: 'gather_silver_sprat',
        name: 'Silver Sprat Run',
        tier: 1,
        type: 'gathering',
        description: 'The fish market needs fresh silver sprat.',
        requirements: [{ itemId: 'raw_silver_sprat', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 90, xp: { fishing: 45 } }
    },
    COOK_SILVER_SPRAT: {
        id: 'cook_silver_sprat',
        name: 'Sprat Fry',
        tier: 1,
        type: 'processing',
        description: 'Prepare cooked silver sprat for the dockside tavern.',
        requirements: [{ itemId: 'cooked_silver_sprat', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 110, xp: { cooking: 55 } }
    },

    // Farming - Crops (higher rewards due to time-gated passive growth)
    GATHER_POTATO: {
        id: 'gather_potato',
        name: 'Potato Harvest',
        tier: 1,
        type: 'gathering',
        description: 'The kitchen needs a fresh potato supply.',
        requirements: [{ itemId: 'potato', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 200, xp: { farming: 160 } }
    },
    GATHER_ONION: {
        id: 'gather_onion',
        name: 'Onion Collection',
        tier: 1,
        type: 'gathering',
        description: 'Onions are needed for the stew pots.',
        requirements: [{ itemId: 'onion', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 220, xp: { farming: 180 } }
    },

    // Herbs
    GATHER_GRIMY_GREENLEAF: {
        id: 'gather_grimy_greenleaf',
        name: 'Green Leaf Foraging',
        tier: 1,
        type: 'gathering',
        description: 'The apothecary needs raw green leaf herbs.',
        requirements: [{ itemId: 'grimy_greenleaf', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 100, xp: { gatherer: 25, farming: 25 } }
    },
    CLEAN_GREENLEAF: {
        id: 'clean_greenleaf',
        name: 'Green Leaf Preparation',
        tier: 1,
        type: 'processing',
        description: 'Clean green leaf herbs for the healers.',
        requirements: [{ itemId: 'clean_greenleaf', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 120, xp: { alchemy: 60 } }
    },
    GATHER_GRIMY_DUSKLEAF: {
        id: 'gather_grimy_duskleaf',
        name: 'Dusk Leaf Gathering',
        tier: 1,
        type: 'gathering',
        description: 'Dusk leaf is prized by alchemists.',
        requirements: [{ itemId: 'grimy_duskleaf', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 110, xp: { gatherer: 28, farming: 27 } }
    },

    // More mobs
    KILL_GOBLIN_SHAMAN: {
        id: 'kill_goblin_shaman',
        name: 'Shaman Hunt',
        tier: 1,
        type: 'kill',
        description: 'The goblin shamans are rallying their kind.',
        requirements: [{ mobId: 'goblin_shaman', targetMin: 10, targetMax: 30 }],
        baseRewards: { gold: 180, xp: { combat: 90 } }
    },
    KILL_GOBLIN_SKIRMISHER: {
        id: 'kill_goblin_skirmisher',
        name: 'Skirmisher Sweep',
        tier: 1,
        type: 'kill',
        description: 'Clear out the goblin skirmishers.',
        requirements: [{ mobId: 'goblin_skirmisher', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 150, xp: { combat: 75 } }
    },

    // ==================== NEW TIER 2 CONTRACTS ====================

    // Ores
    GATHER_GOLD_ORE: {
        id: 'gather_gold_ore',
        name: 'Gold Rush',
        tier: 2,
        type: 'gathering',
        description: 'The treasury needs gold ore for coinage.',
        requirements: [{ itemId: 'gold_ore', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 400, xp: { mining: 175 } }
    },
    SMELT_GOLD: {
        id: 'smelt_gold',
        name: 'Gold Ingots',
        tier: 2,
        type: 'processing',
        description: 'Smelt gold bars for the royal mint.',
        requirements: [{ itemId: 'gold_bar', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 550, xp: { smithing: 225 } }
    },

    // Logs
    GATHER_GOLDLEAF: {
        id: 'gather_goldleaf',
        name: 'Goldleaf Timber',
        tier: 2,
        type: 'gathering',
        description: 'The carpenters need rare goldleaf logs.',
        requirements: [{ itemId: 'goldleaf_logs', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 380, xp: { woodcutting: 165 } }
    },

    // Fish
    GATHER_BLUEBACK: {
        id: 'gather_blueback',
        name: 'Blueback Catch',
        tier: 2,
        type: 'gathering',
        description: 'Blueback fish are in high demand.',
        requirements: [{ itemId: 'raw_blueback', targetMin: 35, targetMax: 100 }],
        baseRewards: { gold: 350, xp: { fishing: 150 } }
    },
    COOK_BLUEBACK: {
        id: 'cook_blueback',
        name: 'Blueback Feast',
        tier: 2,
        type: 'processing',
        description: 'The noble house wants cooked blueback.',
        requirements: [{ itemId: 'cooked_blueback', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 420, xp: { cooking: 175 } }
    },

    // Farming
    GATHER_CABBAGE: {
        id: 'gather_cabbage',
        name: 'Cabbage Delivery',
        tier: 2,
        type: 'gathering',
        description: 'Fresh cabbage for the market.',
        requirements: [{ itemId: 'cabbage', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 700, xp: { farming: 520 } }
    },

    // Herbs
    CLEAN_DUSKLEAF: {
        id: 'clean_duskleaf',
        name: 'Dusk Leaf Processing',
        tier: 2,
        type: 'processing',
        description: 'Process dusk leaf for the alchemists.',
        requirements: [{ itemId: 'clean_duskleaf', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 400, xp: { alchemy: 175 } }
    },
    GATHER_GRIMY_IRONBLOOM: {
        id: 'gather_grimy_ironbloom',
        name: 'Ironbloom Search',
        tier: 2,
        type: 'gathering',
        description: 'Ironbloom is needed for strength potions.',
        requirements: [{ itemId: 'grimy_ironbloom', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 380, xp: { gatherer: 83, farming: 82 } }
    },

    // Potions
    CRAFT_FERAL_DRAFT: {
        id: 'craft_feral_draft',
        name: 'Feral Draft Brewing',
        tier: 2,
        type: 'processing',
        description: 'Warriors need feral drafts for battle.',
        requirements: [{ itemId: 'feral_draft', targetMin: 10, targetMax: 30 }],
        baseRewards: { gold: 500, xp: { alchemy: 200 } }
    },

    // More mobs
    KILL_STRAY_MERCENARY: {
        id: 'kill_stray_mercenary',
        name: 'Mercenary Purge',
        tier: 2,
        type: 'kill',
        description: 'The stray mercenaries are terrorizing travelers.',
        requirements: [{ mobId: 'stray_mercenary', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 650, xp: { combat: 325 } }
    },
    KILL_BANDIT_ARCHER: {
        id: 'kill_bandit_archer',
        name: 'Archer Ambush',
        tier: 2,
        type: 'kill',
        description: 'Bandit archers are picking off travelers.',
        requirements: [{ mobId: 'bandit_archer', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 600, xp: { combat: 300 } }
    },

    // ==================== NEW TIER 3 CONTRACTS ====================

    // Ores
    GATHER_VERDANT_CORE: {
        id: 'gather_verdant_core',
        name: 'Verdant Core Mining',
        tier: 3,
        type: 'gathering',
        description: 'The arcanists need verdant core ore.',
        requirements: [{ itemId: 'verdant_core_ore', targetMin: 80, targetMax: 240 }],
        baseRewards: { gold: 900, xp: { mining: 450 } }
    },
    SMELT_VERDANT_CORE: {
        id: 'smelt_verdant_core',
        name: 'Verdant Core Smelting',
        tier: 3,
        type: 'processing',
        description: 'Smelt verdant core into magical bars.',
        requirements: [{ itemId: 'verdant_core_bar', targetMin: 60, targetMax: 180 }],
        baseRewards: { gold: 1400, xp: { smithing: 700 } }
    },

    // Logs
    GATHER_REDGRAIN: {
        id: 'gather_redgrain',
        name: 'Redgrain Harvest',
        tier: 3,
        type: 'gathering',
        description: 'Redgrain logs are needed for fine furniture.',
        requirements: [{ itemId: 'redgrain_logs', targetMin: 80, targetMax: 240 }],
        baseRewards: { gold: 850, xp: { woodcutting: 425 } }
    },

    // Fish
    GATHER_SKIPJACK: {
        id: 'gather_skipjack',
        name: 'Skipjack Haul',
        tier: 3,
        type: 'gathering',
        description: 'The fishing guild wants skipjack.',
        requirements: [{ itemId: 'raw_skipjack', targetMin: 60, targetMax: 180 }],
        baseRewards: { gold: 1000, xp: { fishing: 500 } }
    },
    COOK_CRIMSON_CHAR: {
        id: 'cook_crimson_char',
        name: 'Crimson Char Cuisine',
        tier: 3,
        type: 'processing',
        description: 'Prepare crimson char for the feast.',
        requirements: [{ itemId: 'cooked_crimson_char', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 1100, xp: { cooking: 550 } }
    },
    COOK_SKIPJACK: {
        id: 'cook_skipjack',
        name: 'Skipjack Preparation',
        tier: 3,
        type: 'processing',
        description: 'Cook skipjack for the sailors.',
        requirements: [{ itemId: 'cooked_skipjack', targetMin: 45, targetMax: 135 }],
        baseRewards: { gold: 1200, xp: { cooking: 600 } }
    },

    // Farming
    GATHER_TOMATO: {
        id: 'gather_tomato',
        name: 'Tomato Harvest',
        tier: 3,
        type: 'gathering',
        description: 'Fresh tomatoes for the kitchens.',
        requirements: [{ itemId: 'tomato', targetMin: 30, targetMax: 90 }],
        baseRewards: { gold: 1800, xp: { farming: 1400 } }
    },

    // Herbs
    GATHER_GRIMY_EMBERROOT: {
        id: 'gather_grimy_emberroot',
        name: 'Emberroot Expedition',
        tier: 3,
        type: 'gathering',
        description: 'Emberroot grows near volcanic vents.',
        requirements: [{ itemId: 'grimy_emberroot', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 900, xp: { gatherer: 225, farming: 225 } }
    },
    CLEAN_IRONBLOOM: {
        id: 'clean_ironbloom',
        name: 'Ironbloom Refinement',
        tier: 3,
        type: 'processing',
        description: 'Refine ironbloom for combat potions.',
        requirements: [{ itemId: 'clean_ironbloom', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 1000, xp: { alchemy: 500 } }
    },

    // Potions
    CRAFT_GUARD_TONIC: {
        id: 'craft_guard_tonic',
        name: 'Guard Tonic Brewing',
        tier: 3,
        type: 'processing',
        description: 'The city guard needs defensive tonics.',
        requirements: [{ itemId: 'guard_tonic', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 1300, xp: { alchemy: 650 } }
    },

    // Arrows
    CRAFT_PYRESTONE_ARROWS: {
        id: 'craft_pyrestone_arrows',
        name: 'Pyrestone Arrow Order',
        tier: 3,
        type: 'processing',
        description: 'The archers need pyrestone arrows.',
        requirements: [{ itemId: 'pyrestone_arrows', targetMin: 200, targetMax: 600 }],
        baseRewards: { gold: 800, xp: { woodworking: 400 } }
    },
    CRAFT_BLACKIRON_ARROWS: {
        id: 'craft_blackiron_arrows',
        name: 'Blackiron Arrow Commission',
        tier: 3,
        type: 'processing',
        description: 'Blackiron arrows pierce heavy armor.',
        requirements: [{ itemId: 'blackiron_arrows', targetMin: 150, targetMax: 450 }],
        baseRewards: { gold: 1100, xp: { woodworking: 550 } }
    },

    // Mobs
    KILL_ROTTING_ZOMBIE: {
        id: 'kill_rotting_zombie',
        name: 'Undead Cleanup',
        tier: 3,
        type: 'kill',
        description: 'Clear the rotting zombies from the crypts.',
        requirements: [{ mobId: 'rotting_zombie', targetMin: 30, targetMax: 75 }],
        baseRewards: { gold: 2000, xp: { combat: 1000 } }
    },
    KILL_CRYPT_ACOLYTE: {
        id: 'kill_crypt_acolyte',
        name: 'Acolyte Purge',
        tier: 3,
        type: 'kill',
        description: 'The crypt acolytes must be stopped.',
        requirements: [{ mobId: 'crypt_acolyte', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 2200, xp: { combat: 1100 } }
    },

    // ==================== NEW TIER 4 CONTRACTS ====================

    // Fish
    GATHER_BROADBILL: {
        id: 'gather_broadbill',
        name: 'Broadbill Expedition',
        tier: 4,
        type: 'gathering',
        description: 'Broadbill swordfish are prized catches.',
        requirements: [{ itemId: 'raw_broadbill', targetMin: 120, targetMax: 360 }],
        baseRewards: { gold: 3500, xp: { fishing: 1750 } }
    },
    COOK_SPINY_LOBSTER: {
        id: 'cook_spiny_lobster',
        name: 'Lobster Banquet',
        tier: 4,
        type: 'processing',
        description: 'Prepare spiny lobster for the royal feast.',
        requirements: [{ itemId: 'cooked_spiny_lobster', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 4500, xp: { cooking: 2250 } }
    },
    COOK_BROADBILL: {
        id: 'cook_broadbill',
        name: 'Broadbill Cuisine',
        tier: 4,
        type: 'processing',
        description: 'Cook broadbill for the admiral.',
        requirements: [{ itemId: 'cooked_broadbill', targetMin: 90, targetMax: 270 }],
        baseRewards: { gold: 4000, xp: { cooking: 2000 } }
    },

    // Farming
    GATHER_CORN: {
        id: 'gather_corn',
        name: 'Corn Harvest',
        tier: 4,
        type: 'gathering',
        description: 'The granary needs corn stores.',
        requirements: [{ itemId: 'corn', targetMin: 40, targetMax: 120 }],
        baseRewards: { gold: 6000, xp: { farming: 5000 } }
    },
    GATHER_STRAWBERRY: {
        id: 'gather_strawberry',
        name: 'Strawberry Picking',
        tier: 4,
        type: 'gathering',
        description: 'Fresh strawberries for the bakery.',
        requirements: [{ itemId: 'strawberry', targetMin: 35, targetMax: 105 }],
        baseRewards: { gold: 7000, xp: { farming: 5600 } }
    },

    // Herbs
    GATHER_GRIMY_DEEPVINE: {
        id: 'gather_grimy_deepvine',
        name: 'Deepvine Delving',
        tier: 4,
        type: 'gathering',
        description: 'Deepvine grows in the darkest caves.',
        requirements: [{ itemId: 'grimy_deepvine', targetMin: 75, targetMax: 225 }],
        baseRewards: { gold: 3200, xp: { gatherer: 800, farming: 800 } }
    },
    CLEAN_EMBERROOT: {
        id: 'clean_emberroot',
        name: 'Emberroot Processing',
        tier: 4,
        type: 'processing',
        description: 'Process emberroot for advanced potions.',
        requirements: [{ itemId: 'clean_emberroot', targetMin: 60, targetMax: 180 }],
        baseRewards: { gold: 3500, xp: { alchemy: 1750 } }
    },

    // Potions
    CRAFT_BALANCED_INFUSION: {
        id: 'craft_balanced_infusion',
        name: 'Balanced Infusion Order',
        tier: 4,
        type: 'processing',
        description: 'The elite guard needs balanced infusions.',
        requirements: [{ itemId: 'balanced_infusion', targetMin: 20, targetMax: 60 }],
        baseRewards: { gold: 5000, xp: { alchemy: 2500 } }
    },

    // Arrows
    CRAFT_SKYIRON_ARROWS: {
        id: 'craft_skyiron_arrows',
        name: 'Skyiron Arrow Forging',
        tier: 4,
        type: 'processing',
        description: 'Skyiron arrows for the elite archers.',
        requirements: [{ itemId: 'skyiron_arrows', targetMin: 250, targetMax: 750 }],
        baseRewards: { gold: 3500, xp: { woodworking: 1750 } }
    },

    // Mobs
    KILL_FLAME_IMP: {
        id: 'kill_flame_imp',
        name: 'Imp Infestation',
        tier: 4,
        type: 'kill',
        description: 'Flame imps are swarming the mines.',
        requirements: [{ mobId: 'flame_imp', targetMin: 35, targetMax: 90 }],
        baseRewards: { gold: 7000, xp: { combat: 3500 } }
    },
    KILL_WYRMLING: {
        id: 'kill_wyrmling',
        name: 'Wyrmling Nest',
        tier: 4,
        type: 'kill',
        description: 'Clear the wyrmling nest before they mature.',
        requirements: [{ mobId: 'wyrmling', targetMin: 30, targetMax: 80 }],
        baseRewards: { gold: 8000, xp: { combat: 4000 } }
    },
    KILL_DRACONIC_SENTINEL: {
        id: 'kill_draconic_sentinel',
        name: 'Sentinel Strike',
        tier: 4,
        type: 'kill',
        description: 'The draconic sentinels guard forbidden treasures.',
        requirements: [{ mobId: 'draconic_sentinel', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 9000, xp: { combat: 4500 } }
    },

    // ==================== NEW TIER 5 CONTRACTS ====================

    // Fish
    COOK_MAKO: {
        id: 'cook_mako',
        name: 'Mako Mastery',
        tier: 5,
        type: 'processing',
        description: 'Prepare the legendary mako shark.',
        requirements: [{ itemId: 'cooked_mako', targetMin: 200, targetMax: 600 }],
        baseRewards: { gold: 22000, xp: { cooking: 11000 } }
    },

    // Farming
    GATHER_WATERMELON: {
        id: 'gather_watermelon',
        name: 'Watermelon Harvest',
        tier: 5,
        type: 'gathering',
        description: 'Watermelons for the summer festival.',
        requirements: [{ itemId: 'watermelon', targetMin: 50, targetMax: 150 }],
        baseRewards: { gold: 28000, xp: { farming: 24000 } }
    },
    GATHER_BRIGHTMAW: {
        id: 'gather_brightmaw',
        name: 'Brightmaw Collection',
        tier: 5,
        type: 'gathering',
        description: 'Brightmaw is a rare magical crop.',
        requirements: [{ itemId: 'grimy_brightmaw', targetMin: 35, targetMax: 105 }],
        baseRewards: { gold: 35000, xp: { farming: 32000 } }
    },
    GATHER_KINGS_FRUIT: {
        id: 'gather_kings_fruit',
        name: "King's Fruit Harvest",
        tier: 5,
        type: 'gathering',
        description: "The legendary king's fruit for the royal gardens.",
        requirements: [{ itemId: 'kings_fruit', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 45000, xp: { farming: 40000 } }
    },

    // Herbs
    GATHER_GRIMY_ASHBLOOM: {
        id: 'gather_grimy_ashbloom',
        name: 'Ashbloom Expedition',
        tier: 5,
        type: 'gathering',
        description: 'Ashbloom grows only in volcanic ash.',
        requirements: [{ itemId: 'grimy_ashbloom', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 14000, xp: { gatherer: 3500, farming: 3500 } }
    },
    GATHER_GRIMY_CROWNWORT: {
        id: 'gather_grimy_crownwort',
        name: 'Crownwort Search',
        tier: 5,
        type: 'gathering',
        description: 'Crownwort is the rarest herb.',
        requirements: [{ itemId: 'grimy_crownwort', targetMin: 80, targetMax: 240 }],
        baseRewards: { gold: 18000, xp: { gatherer: 4500, farming: 4500 } }
    },
    CLEAN_DEEPVINE: {
        id: 'clean_deepvine',
        name: 'Deepvine Refinement',
        tier: 5,
        type: 'processing',
        description: 'Refine deepvine for master potions.',
        requirements: [{ itemId: 'clean_deepvine', targetMin: 100, targetMax: 300 }],
        baseRewards: { gold: 16000, xp: { alchemy: 8000 } }
    },
    CLEAN_ASHBLOOM: {
        id: 'clean_ashbloom',
        name: 'Ashbloom Processing',
        tier: 5,
        type: 'processing',
        description: 'Process ashbloom for elite potions.',
        requirements: [{ itemId: 'clean_ashbloom', targetMin: 80, targetMax: 240 }],
        baseRewards: { gold: 18000, xp: { alchemy: 9000 } }
    },

    // Potions
    CRAFT_ASCENDANT_CONCOCTION: {
        id: 'craft_ascendant_concoction',
        name: 'Ascendant Brewing',
        tier: 5,
        type: 'processing',
        description: 'Brew the legendary ascendant concoction.',
        requirements: [{ itemId: 'ascendant_concoction', targetMin: 25, targetMax: 75 }],
        baseRewards: { gold: 25000, xp: { alchemy: 12500 } }
    },

    // Arrows
    CRAFT_VOIDSTEEL_ARROWS: {
        id: 'craft_voidsteel_arrows',
        name: 'Voidsteel Arrow Mastery',
        tier: 5,
        type: 'processing',
        description: 'Craft the ultimate voidsteel arrows.',
        requirements: [{ itemId: 'voidsteel_arrows', targetMin: 400, targetMax: 1200 }],
        baseRewards: { gold: 18000, xp: { woodworking: 9000 } }
    },

    // Mobs
    KILL_VOID_ABERRATION: {
        id: 'kill_void_aberration',
        name: 'Void Aberration Hunt',
        tier: 5,
        type: 'kill',
        description: 'The void aberrations threaten reality itself.',
        requirements: [{ mobId: 'void_aberration', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 45000, xp: { combat: 22500 } }
    },
    KILL_VOID_CHANNELER: {
        id: 'kill_void_channeler',
        name: 'Channeler Containment',
        tier: 5,
        type: 'kill',
        description: 'Stop the void channelers before they open more rifts.',
        requirements: [{ mobId: 'void_channeler', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 50000, xp: { combat: 25000 } }
    },

    // ==================== CONDITIONAL CONTRACTS (Bosses & Special Mobs) ====================

    // Dungeon Bosses (requires Ashen Crucible Tier 1 access)
    KILL_ASHEN_WARDEN: {
        id: 'kill_ashen_warden',
        name: 'Warden Slaying',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Ashen Warden.',
        requirements: [{ mobId: 'ashen_warden', targetMin: 3, targetMax: 8 }],
        baseRewards: { gold: 35000, xp: { combat: 17500 } },
        condition: { questUnlock: 'zone_ashen_crucible_t1' }
    },
    KILL_PYRE_GUARDIAN: {
        id: 'kill_pyre_guardian',
        name: 'Guardian Challenge',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Pyre Guardian.',
        requirements: [{ mobId: 'pyre_guardian', targetMin: 3, targetMax: 8 }],
        baseRewards: { gold: 40000, xp: { combat: 20000 } },
        condition: { questUnlock: 'zone_ashen_crucible_t1' }
    },
    KILL_FLAME_ETERNAL: {
        id: 'kill_flame_eternal',
        name: 'Eternal Flame',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Flame Eternal.',
        requirements: [{ mobId: 'flame_eternal', targetMin: 2, targetMax: 6 }],
        baseRewards: { gold: 45000, xp: { combat: 22500 } },
        condition: { questUnlock: 'zone_ashen_crucible_t1' }
    },

    // Set Bosses (requires Ashen Crucible Tier 2 access)
    KILL_SEARING_MATRIARCH: {
        id: 'kill_searing_matriarch',
        name: 'Matriarch Hunt',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Searing Matriarch.',
        requirements: [{ mobId: 'searing_matriarch', targetMin: 2, targetMax: 5 }],
        baseRewards: { gold: 55000, xp: { combat: 27500 } },
        condition: { questUnlock: 'zone_ashen_crucible_t2' }
    },
    KILL_MOLTEN_COLOSSUS: {
        id: 'kill_molten_colossus',
        name: 'Colossus Challenge',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Molten Colossus.',
        requirements: [{ mobId: 'molten_colossus', targetMin: 2, targetMax: 5 }],
        baseRewards: { gold: 58000, xp: { combat: 29000 } },
        condition: { questUnlock: 'zone_ashen_crucible_t2' }
    },
    KILL_CINDERED_KNIGHT: {
        id: 'kill_cindered_knight',
        name: 'Knight\'s End',
        tier: 5,
        type: 'kill',
        description: 'Defeat the Cindered Knight.',
        requirements: [{ mobId: 'cindered_knight', targetMin: 2, targetMax: 5 }],
        baseRewards: { gold: 60000, xp: { combat: 30000 } },
        condition: { questUnlock: 'zone_ashen_crucible_t2' }
    },

    // Convergence Herald (requires Tier 3 access)
    KILL_CONVERGENCE_HERALD: {
        id: 'kill_convergence_herald',
        name: 'Herald of Convergence',
        tier: 5,
        type: 'kill',
        description: 'Defeat the ultimate boss: Convergence Herald.',
        requirements: [{ mobId: 'convergence_herald', targetMin: 1, targetMax: 3 }],
        baseRewards: { gold: 100000, xp: { combat: 50000 } },
        condition: { questUnlock: 'zone_ashen_crucible_t3' }
    },

    // Themed Pyre Veil Mobs (requires pyre level)
    KILL_EMBER_GOBLIN: {
        id: 'kill_ember_goblin',
        name: 'Ember Goblin Purge',
        tier: 4,
        type: 'kill',
        description: 'Clear the ember goblins from the Pyre Veil.',
        requirements: [{ mobId: 'ember_goblin', targetMin: 40, targetMax: 100 }],
        baseRewards: { gold: 6000, xp: { combat: 3000 } },
        condition: { pyreLevel: 45 }
    },
    KILL_ASHBOUND_TROLL: {
        id: 'kill_ashbound_troll',
        name: 'Ashbound Troll Hunt',
        tier: 4,
        type: 'kill',
        description: 'The ashbound trolls guard the deeper veil.',
        requirements: [{ mobId: 'ashbound_troll', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 7500, xp: { combat: 3750 } },
        condition: { pyreLevel: 55 }
    },
    KILL_SCORCHED_REVENANT: {
        id: 'kill_scorched_revenant',
        name: 'Revenant Cleansing',
        tier: 4,
        type: 'kill',
        description: 'Scorched revenants haunt the ashen wastes.',
        requirements: [{ mobId: 'scorched_revenant', targetMin: 30, targetMax: 75 }],
        baseRewards: { gold: 8000, xp: { combat: 4000 } },
        condition: { pyreLevel: 60 }
    },
    KILL_CINDERBOUND_CULTIST: {
        id: 'kill_cinderbound_cultist',
        name: 'Cult Disruption',
        tier: 5,
        type: 'kill',
        description: 'The cinderbound cultists worship the flame.',
        requirements: [{ mobId: 'cinderbound_cultist', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 40000, xp: { combat: 20000 } },
        condition: { pyreLevel: 70 }
    },
    KILL_MOLTEN_WYRM: {
        id: 'kill_molten_wyrm',
        name: 'Wyrm Extermination',
        tier: 5,
        type: 'kill',
        description: 'Molten wyrms threaten the inner sanctum.',
        requirements: [{ mobId: 'molten_wyrm', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 48000, xp: { combat: 24000 } },
        condition: { pyreLevel: 75 }
    },
    KILL_VOID_ASH_HORROR: {
        id: 'kill_void_ash_horror',
        name: 'Horror Hunt',
        tier: 5,
        type: 'kill',
        description: 'The void ash horrors are the most dangerous creatures.',
        requirements: [{ mobId: 'void_ash_horror', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 55000, xp: { combat: 27500 } },
        condition: { pyreLevel: 85 }
    },

    // ==================== MISSING MOB CONTRACTS ====================
    // 14 existing mobs that previously had no bounty contracts

    // Goblin Camp (Tier 1)
    KILL_CAMP_CHICKEN: {
        id: 'kill_camp_chicken',
        name: 'Chicken Roundup',
        tier: 1,
        type: 'kill',
        description: 'The camp chickens are getting out of hand.',
        requirements: [{ mobId: 'camp_chicken', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 60, xp: { combat: 30 } }
    },
    KILL_GOBLIN_HERBALIST: {
        id: 'kill_goblin_herbalist',
        name: 'Herbalist Raid',
        tier: 1,
        type: 'kill',
        description: 'The goblin herbalists are poisoning the water supply.',
        requirements: [{ mobId: 'goblin_herbalist', targetMin: 15, targetMax: 35 }],
        baseRewards: { gold: 170, xp: { combat: 85 } }
    },

    // Forgotten Outskirts (Tier 2)
    KILL_CARRION_HAWK: {
        id: 'kill_carrion_hawk',
        name: 'Hawk Culling',
        tier: 2,
        type: 'kill',
        description: 'Carrion hawks are attacking travelers on the outskirts.',
        requirements: [{ mobId: 'carrion_hawk', targetMin: 25, targetMax: 55 }],
        baseRewards: { gold: 500, xp: { combat: 250 } }
    },

    // Bandit Hideout (Tier 3)
    KILL_BANDIT_ALCHEMIST: {
        id: 'kill_bandit_alchemist',
        name: 'Alchemist Takedown',
        tier: 3,
        type: 'kill',
        description: 'Bandit alchemists are brewing dangerous concoctions.',
        requirements: [{ mobId: 'bandit_alchemist', targetMin: 30, targetMax: 75 }],
        baseRewards: { gold: 1800, xp: { combat: 900 } }
    },
    KILL_BLACK_MARKET_RUNNER: {
        id: 'kill_black_market_runner',
        name: 'Black Market Bust',
        tier: 3,
        type: 'kill',
        description: 'Stop the black market runners from smuggling goods.',
        requirements: [{ mobId: 'black_market_runner', targetMin: 30, targetMax: 70 }],
        baseRewards: { gold: 2000, xp: { combat: 1000 } }
    },

    // Troll Caves (Tier 3)
    KILL_REGENERATING_TROLL: {
        id: 'kill_regenerating_troll',
        name: 'Troll Purge',
        tier: 3,
        type: 'kill',
        description: 'The regenerating trolls are impossible to keep down.',
        requirements: [{ mobId: 'regenerating_troll', targetMin: 30, targetMax: 65 }],
        baseRewards: { gold: 2100, xp: { combat: 1050 } }
    },
    KILL_CRYSTAL_LURKER: {
        id: 'kill_crystal_lurker',
        name: 'Crystal Lurker Hunt',
        tier: 3,
        type: 'kill',
        description: 'Crystal lurkers guard the gem veins in the caves.',
        requirements: [{ mobId: 'crystal_lurker', targetMin: 25, targetMax: 60 }],
        baseRewards: { gold: 2300, xp: { combat: 1150 } }
    },

    // Undead Crypt (Tier 4)
    KILL_TOMB_WEAVER: {
        id: 'kill_tomb_weaver',
        name: 'Tomb Weaver Purge',
        tier: 4,
        type: 'kill',
        description: 'Tomb weavers are spinning cursed webs throughout the crypt.',
        requirements: [{ mobId: 'tomb_weaver', targetMin: 35, targetMax: 85 }],
        baseRewards: { gold: 7000, xp: { combat: 3500 } }
    },

    // Demon Scar (Tier 4)
    KILL_INFERNAL_ARCANIST: {
        id: 'kill_infernal_arcanist',
        name: 'Arcanist Purge',
        tier: 4,
        type: 'kill',
        description: 'Infernal arcanists are channeling dark magic from the scar.',
        requirements: [{ mobId: 'infernal_arcanist', targetMin: 35, targetMax: 90 }],
        baseRewards: { gold: 8500, xp: { combat: 4250 } }
    },

    // Dragon Lair (Tier 4)
    KILL_HOARD_GUARDIAN: {
        id: 'kill_hoard_guardian',
        name: 'Guardian Assault',
        tier: 4,
        type: 'kill',
        description: 'The hoard guardians protect the dragon\'s treasure.',
        requirements: [{ mobId: 'hoard_guardian', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 9500, xp: { combat: 4750 } }
    },

    // Void Rift (Tier 5)
    KILL_RIFT_WEAVER: {
        id: 'kill_rift_weaver',
        name: 'Rift Weaver Hunt',
        tier: 5,
        type: 'kill',
        description: 'Rift weavers tear holes in reality wherever they roam.',
        requirements: [{ mobId: 'rift_weaver', targetMin: 12, targetMax: 30 }],
        baseRewards: { gold: 52000, xp: { combat: 26000 } }
    },

    // Ancient Sanctum (Tier 5)
    KILL_ELDER_HERBKEEPER: {
        id: 'kill_elder_herbkeeper',
        name: 'Herbkeeper Hunt',
        tier: 5,
        type: 'kill',
        description: 'The elder herbkeepers guard the rarest botanical secrets.',
        requirements: [{ mobId: 'elder_herbkeeper', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 58000, xp: { combat: 29000 } }
    },
    KILL_CORRUPTED_AVATAR: {
        id: 'kill_corrupted_avatar',
        name: 'Avatar Slaying',
        tier: 5,
        type: 'kill',
        description: 'The corrupted avatars channel eldritch energies.',
        requirements: [{ mobId: 'corrupted_avatar', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 60000, xp: { combat: 30000 } }
    },
    KILL_ELDER_GOD_FRAGMENT: {
        id: 'kill_elder_god_fragment',
        name: 'Fragment Destruction',
        tier: 5,
        type: 'kill',
        description: 'Destroy the fragments of the elder god before they reform.',
        requirements: [{ mobId: 'elder_god_fragment', targetMin: 8, targetMax: 20 }],
        baseRewards: { gold: 65000, xp: { combat: 32500 } }
    },

    // ========== New Mob Contracts ==========

    // Goblin Camp - Tier 1
    KILL_GOBLIN_TRAPPER: {
        id: 'kill_goblin_trapper',
        name: 'Disarm the Trappers',
        tier: 1,
        type: 'kill',
        description: 'Goblin trappers have rigged the forest paths with snares.',
        requirements: [{ mobId: 'goblin_trapper', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 160, xp: { combat: 80 } }
    },
    KILL_GOBLIN_LOOTER: {
        id: 'kill_goblin_looter',
        name: 'Looter Crackdown',
        tier: 1,
        type: 'kill',
        description: 'Goblin looters are raiding the merchant caravans at dawn.',
        requirements: [{ mobId: 'goblin_looter', targetMin: 15, targetMax: 40 }],
        baseRewards: { gold: 140, xp: { combat: 70 } }
    },
    KILL_GOBLIN_FIRESTARTER: {
        id: 'kill_goblin_firestarter',
        name: 'Firestarter Suppression',
        tier: 1,
        type: 'kill',
        description: 'Goblin firestarters are setting the farmlands ablaze.',
        requirements: [{ mobId: 'goblin_firestarter', targetMin: 10, targetMax: 35 }],
        baseRewards: { gold: 190, xp: { combat: 95 } }
    },

    // Forgotten Outskirts - Tier 2
    KILL_WILD_HOUND: {
        id: 'kill_wild_hound',
        name: 'Hound Pack Cull',
        tier: 2,
        type: 'kill',
        description: 'Packs of wild hounds are terrorizing the outskirt settlements.',
        requirements: [{ mobId: 'wild_hound', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 550, xp: { combat: 275 } }
    },
    KILL_BANDIT_HEXER: {
        id: 'kill_bandit_hexer',
        name: 'Hexer Silencing',
        tier: 2,
        type: 'kill',
        description: 'Bandit hexers are cursing anyone who crosses their territory.',
        requirements: [{ mobId: 'bandit_hexer', targetMin: 15, targetMax: 45 }],
        baseRewards: { gold: 700, xp: { combat: 350 } }
    },
    KILL_RUINED_SENTRY: {
        id: 'kill_ruined_sentry',
        name: 'Sentry Demolition',
        tier: 2,
        type: 'kill',
        description: 'Reanimated sentries patrol the ruins, attacking all who approach.',
        requirements: [{ mobId: 'ruined_sentry', targetMin: 20, targetMax: 45 }],
        baseRewards: { gold: 650, xp: { combat: 325 } }
    },

    // Bandit Hideout - Tier 3
    KILL_BANDIT_SCOUT: {
        id: 'kill_bandit_scout',
        name: 'Scout Elimination',
        tier: 3,
        type: 'kill',
        description: 'Bandit scouts are mapping patrol routes for the hideout.',
        requirements: [{ mobId: 'bandit_scout', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 2100, xp: { combat: 1050 } }
    },
    KILL_SCALED_MARAUDER: {
        id: 'kill_scaled_marauder',
        name: 'Marauder Ambush',
        tier: 3,
        type: 'kill',
        description: 'Scaled marauders are plundering supply wagons on the hidden trail.',
        requirements: [{ mobId: 'scaled_marauder', targetMin: 25, targetMax: 60 }],
        baseRewards: { gold: 2300, xp: { combat: 1150 } }
    },

    // Troll Caves - Tier 3
    KILL_CAVE_SPIDER: {
        id: 'kill_cave_spider',
        name: 'Spider Nest Purge',
        tier: 3,
        type: 'kill',
        description: 'Venomous cave spiders have infested the lower tunnels.',
        requirements: [{ mobId: 'cave_spider', targetMin: 30, targetMax: 70 }],
        baseRewards: { gold: 1900, xp: { combat: 950 } }
    },
    KILL_STONE_GOLEM: {
        id: 'kill_stone_golem',
        name: 'Golem Smashing',
        tier: 3,
        type: 'kill',
        description: 'Ancient stone golems have awakened and block the mining corridors.',
        requirements: [{ mobId: 'stone_golem', targetMin: 20, targetMax: 55 }],
        baseRewards: { gold: 2400, xp: { combat: 1200 } }
    },
    KILL_CURSED_KNIGHT: {
        id: 'kill_cursed_knight',
        name: 'Knight\'s Requiem',
        tier: 3,
        type: 'kill',
        description: 'Cursed knights wander the deep caves, bound by an ancient oath.',
        requirements: [{ mobId: 'cursed_knight', targetMin: 20, targetMax: 50 }],
        baseRewards: { gold: 2500, xp: { combat: 1250 } }
    },

    // Undead Crypt - Tier 4
    KILL_SPECTRAL_ARCHER: {
        id: 'kill_spectral_archer',
        name: 'Spectral Silencing',
        tier: 4,
        type: 'kill',
        description: 'Spectral archers rain ghostly arrows from the crypt balconies.',
        requirements: [{ mobId: 'spectral_archer', targetMin: 30, targetMax: 80 }],
        baseRewards: { gold: 7500, xp: { combat: 3750 } }
    },
    KILL_BONE_COLLECTOR: {
        id: 'kill_bone_collector',
        name: 'Bone Collector Hunt',
        tier: 4,
        type: 'kill',
        description: 'Bone collectors scavenge remains to build undead abominations.',
        requirements: [{ mobId: 'bone_collector', targetMin: 25, targetMax: 70 }],
        baseRewards: { gold: 8000, xp: { combat: 4000 } }
    },
    KILL_SCORCHED_REVENANT_CRYPT: {
        id: 'kill_scorched_revenant_crypt',
        name: 'Revenant Purge',
        tier: 4,
        type: 'kill',
        description: 'Scorched revenants burn with undying fury in the deepest crypts.',
        requirements: [{ mobId: 'scorched_revenant', targetMin: 30, targetMax: 75 }],
        baseRewards: { gold: 7800, xp: { combat: 3900 } }
    },

    // Demon Scar - Tier 4
    KILL_PIT_FIEND: {
        id: 'kill_pit_fiend',
        name: 'Fiend Banishment',
        tier: 4,
        type: 'kill',
        description: 'Pit fiends crawl forth from the smoldering scar to wreak havoc.',
        requirements: [{ mobId: 'pit_fiend', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 9000, xp: { combat: 4500 } }
    },
    KILL_HELLHOUND: {
        id: 'kill_hellhound',
        name: 'Hellhound Cull',
        tier: 4,
        type: 'kill',
        description: 'Packs of hellhounds prowl the scorched wastes, hunting anything alive.',
        requirements: [{ mobId: 'hellhound', targetMin: 30, targetMax: 80 }],
        baseRewards: { gold: 8500, xp: { combat: 4250 } }
    },
    KILL_SOUL_HARVESTER: {
        id: 'kill_soul_harvester',
        name: 'Soul Harvest',
        tier: 4,
        type: 'kill',
        description: 'Soul harvesters drain the life from anything that enters the Demon Scar.',
        requirements: [{ mobId: 'soul_harvester', targetMin: 35, targetMax: 90 }],
        baseRewards: { gold: 7000, xp: { combat: 3500 } }
    },

    // Dragon Lair - Tier 4
    KILL_DRAKE_WHELP: {
        id: 'kill_drake_whelp',
        name: 'Whelp Thinning',
        tier: 4,
        type: 'kill',
        description: 'Drake whelps are growing bolder, venturing out of the lair to hunt.',
        requirements: [{ mobId: 'drake_whelp', targetMin: 25, targetMax: 65 }],
        baseRewards: { gold: 8000, xp: { combat: 4000 } }
    },
    KILL_DRAGON_PRIEST: {
        id: 'kill_dragon_priest',
        name: 'Priest Purge',
        tier: 4,
        type: 'kill',
        description: 'Dragon priests channel ancient draconic magic from the inner sanctum.',
        requirements: [{ mobId: 'dragon_priest', targetMin: 20, targetMax: 55 }],
        baseRewards: { gold: 9500, xp: { combat: 4750 } }
    },
    KILL_DRACONIC_SENTINEL_LAIR: {
        id: 'kill_draconic_sentinel_lair',
        name: 'Sentinel Sortie',
        tier: 4,
        type: 'kill',
        description: 'Draconic sentinels guard the outer chambers with ruthless efficiency.',
        requirements: [{ mobId: 'draconic_sentinel', targetMin: 25, targetMax: 60 }],
        baseRewards: { gold: 8800, xp: { combat: 4400 } }
    },

    // Void Rift - Tier 5
    KILL_VOID_STALKER: {
        id: 'kill_void_stalker',
        name: 'Void Stalker Pursuit',
        tier: 5,
        type: 'kill',
        description: 'Void stalkers phase between dimensions, striking without warning.',
        requirements: [{ mobId: 'void_stalker', targetMin: 12, targetMax: 30 }],
        baseRewards: { gold: 48000, xp: { combat: 24000 } }
    },
    KILL_ENTROPY_MAW: {
        id: 'kill_entropy_maw',
        name: 'Entropy Devourer',
        tier: 5,
        type: 'kill',
        description: 'Entropy maws consume everything around them, leaving only void.',
        requirements: [{ mobId: 'entropy_maw', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 55000, xp: { combat: 27500 } }
    },
    KILL_TEMPORAL_SHADE: {
        id: 'kill_temporal_shade',
        name: 'Shade of Lost Time',
        tier: 5,
        type: 'kill',
        description: 'Temporal shades trap their victims in endless time loops.',
        requirements: [{ mobId: 'temporal_shade', targetMin: 10, targetMax: 28 }],
        baseRewards: { gold: 52000, xp: { combat: 26000 } }
    },

    // Ancient Sanctum - Tier 5
    KILL_SANCTUM_GUARDIAN: {
        id: 'kill_sanctum_guardian',
        name: 'Sanctum Vanguard',
        tier: 5,
        type: 'kill',
        description: 'Sanctum guardians have stood vigil for millennia and will not yield.',
        requirements: [{ mobId: 'sanctum_guardian', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 58000, xp: { combat: 29000 } }
    },
    KILL_ANCIENT_SCRIBE: {
        id: 'kill_ancient_scribe',
        name: 'Scribe Silencing',
        tier: 5,
        type: 'kill',
        description: 'Ancient scribes channel forbidden knowledge to empower the sanctum defenses.',
        requirements: [{ mobId: 'ancient_scribe', targetMin: 12, targetMax: 30 }],
        baseRewards: { gold: 46000, xp: { combat: 23000 } }
    },
    KILL_ELDER_CONSTRUCT_SENTRY: {
        id: 'kill_elder_construct_sentry',
        name: 'Construct Patrol',
        tier: 5,
        type: 'kill',
        description: 'Elder constructs patrol the inner halls, crushing intruders on sight.',
        requirements: [{ mobId: 'elder_construct', targetMin: 8, targetMax: 22 }],
        baseRewards: { gold: 62000, xp: { combat: 31000 } }
    },
    KILL_RELIC_HOARDER: {
        id: 'kill_relic_hoarder',
        name: 'Hoarder\'s Downfall',
        tier: 5,
        type: 'kill',
        description: 'Relic hoarders guard ancient artifacts with obsessive fury.',
        requirements: [{ mobId: 'relic_hoarder', targetMin: 10, targetMax: 25 }],
        baseRewards: { gold: 56000, xp: { combat: 28000 } }
    },
    KILL_RIFT_SCAVENGER: {
        id: 'kill_rift_scavenger',
        name: 'Scavenger Sweep',
        tier: 5,
        type: 'kill',
        description: 'Rift scavengers pick apart anything the void spits out, growing stronger each time.',
        requirements: [{ mobId: 'rift_scavenger', targetMin: 8, targetMax: 20 }],
        baseRewards: { gold: 64000, xp: { combat: 32000 } }
    }
};

/**
 * Generate a random target value within range, rounded to nearest 5
 * @param {number} min - Minimum target
 * @param {number} max - Maximum target
 * @returns {number} Random target ending in 0 or 5
 */
export function generateRandomTarget(min, max) {
    // Calculate how many "steps of 5" are available
    const minStep = Math.ceil(min / 5);
    const maxStep = Math.floor(max / 5);
    // Pick a random step
    const step = minStep + Math.floor(Math.random() * (maxStep - minStep + 1));
    return step * 5;
}

/**
 * Generate a contract instance with randomized requirements and scaled rewards
 * @param {Object} baseContract - Base contract definition
 * @returns {Object} Generated contract with fixed targets and scaled rewards
 */
export function generateContractInstance(baseContract) {
    // Deep clone the base contract
    const instance = JSON.parse(JSON.stringify(baseContract));

    // Track reward scaling based on average requirement ratio
    let totalRatio = 0;
    let reqCount = 0;

    // Generate random targets for each requirement
    instance.requirements = baseContract.requirements.map(req => {
        const newReq = { ...req };
        if (req.targetMin !== undefined && req.targetMax !== undefined) {
            newReq.target = generateRandomTarget(req.targetMin, req.targetMax);
            // Calculate ratio for reward scaling (how far between min and max)
            const range = req.targetMax - req.targetMin;
            const ratio = range > 0 ? (newReq.target - req.targetMin) / range : 0.5;
            totalRatio += ratio;
            reqCount++;
            // Remove min/max from instance
            delete newReq.targetMin;
            delete newReq.targetMax;
        } else if (req.target) {
            // Fallback for legacy fixed target
            newReq.target = req.target;
            totalRatio += 0.5;
            reqCount++;
        }
        return newReq;
    });

    // Calculate average ratio for reward scaling
    const avgRatio = reqCount > 0 ? totalRatio / reqCount : 0.5;
    // Scale rewards: 0.7x at min, 1.3x at max (linear interpolation)
    const rewardMultiplier = 0.7 + (avgRatio * 0.6);

    // Scale rewards based on requirement ratio
    instance.rewards = {};
    const base = baseContract.baseRewards || baseContract.rewards || {};

    if (base.gold) {
        // Round gold to nearest 5
        instance.rewards.gold = Math.round((base.gold * rewardMultiplier) / 5) * 5;
    }
    if (base.xp) {
        instance.rewards.xp = {};
        for (const [skill, xp] of Object.entries(base.xp)) {
            // Round XP to nearest 5
            instance.rewards.xp[skill] = Math.round((xp * rewardMultiplier) / 5) * 5;
        }
    }

    // Add generation timestamp
    instance.generatedAt = Date.now();

    return instance;
}

/**
 * Contract Tier Configuration
 *
 * PRICING SYSTEM (Gold Sink):
 * - Contracts cost gold upfront to accept
 * - Base contracts are net negative (cost > reward)
 * - Wealth talent branch makes contracts profitable when procs hit
 * - This creates meaningful investment in the Wealth talent tree
 *
 * | Tier | Cost | Avg Reward | Net (no talents) |
 * |------|------|------------|------------------|
 * | T1   | 150g | ~100g      | -50g             |
 * | T2   | 500g | ~350g      | -150g            |
 * | T3   | 1,500g | ~1,000g  | -500g            |
 * | T4   | 7,500g | ~5,000g  | -2,500g          |
 * | T5   | 35,000g | ~25,000g | -10,000g        |
 */
export const CONTRACT_TIERS = {
    1: {
        bountyLevel: 1,
        skillLevel: [1, 20],
        quantities: [25, 50],
        goldReward: [50, 200],
        xpMultiplier: 1.0,
        cost: 150  // Net -50g average
    },
    2: {
        bountyLevel: 15,
        skillLevel: [20, 40],
        quantities: [75, 150],
        goldReward: [300, 800],
        xpMultiplier: 1.5,
        cost: 500  // Net -150g average
    },
    3: {
        bountyLevel: 30,
        skillLevel: [40, 60],
        quantities: [200, 400],
        goldReward: [1000, 3000],
        xpMultiplier: 2.0,
        cost: 1500  // Net -500g average
    },
    4: {
        bountyLevel: 50,
        skillLevel: [60, 80],
        quantities: [500, 1000],
        goldReward: [5000, 15000],
        xpMultiplier: 3.0,
        cost: 7500  // Net -2,500g average
    },
    5: {
        bountyLevel: 70,
        skillLevel: [80, 99],
        quantities: [1500, 3000],
        goldReward: [25000, 50000],
        xpMultiplier: 5.0,
        cost: 35000  // Net -10,000g average
    }
};

/**
 * Bounty skill configuration
 */
export const BOUNTY_CONFIG = {
    MAX_ACTIVE_CONTRACTS: 3,  // Maximum active contracts at once
    CONTRACTS_DISPLAYED_PER_TIER: 12,  // Only show this many contracts per tier (creates rotation)
    MAX_ABANDONS_BEFORE_COMPLETE: 2,  // Must complete a contract to reset abandon counter
    XP_PER_CONTRACT_TIER: {
        1: 50,
        2: 150,
        3: 400,
        4: 1000,
        5: 3000
    },
    // Multipliers based on contract type effort
    // Gathering: base, Kill: combat risk, Processing: gather + process
    XP_TYPE_MULTIPLIER: {
        gathering: 1.0,
        kill: 1.2,
        processing: 1.4
    }
};

/**
 * Calculate bounty XP for a contract based on tier and type
 * @param {Object} contract - Contract definition
 * @returns {number} Bounty XP amount
 */
export function getBountyXP(contract) {
    const baseXP = BOUNTY_CONFIG.XP_PER_CONTRACT_TIER[contract.tier] || 50;
    const typeMultiplier = BOUNTY_CONFIG.XP_TYPE_MULTIPLIER[contract.type] || 1.0;
    return Math.round(baseXP * typeMultiplier);
}

// ==================== BOUNTY MARKS ====================
// Per-mob kill progression that boosts contract rewards and combat bonuses

export const BOUNTY_MARKS = [
    { rank: 0, title: null,        kills: 0,     bountyBonus: 0,    dropRate: 0,     accuracy: 0, defIgnore: 0 },
    { rank: 1, title: 'Noted',     kills: 25,    bountyBonus: 0.03, dropRate: 0,     accuracy: 0, defIgnore: 0 },
    { rank: 2, title: 'Tracked',   kills: 75,    bountyBonus: 0.06, dropRate: 0,     accuracy: 0, defIgnore: 0 },
    { rank: 3, title: 'Marked',    kills: 200,   bountyBonus: 0.10, dropRate: 0.01,  accuracy: 1, defIgnore: 0 },
    { rank: 4, title: 'Hunted',    kills: 500,   bountyBonus: 0.14, dropRate: 0.015, accuracy: 2, defIgnore: 0 },
    { rank: 5, title: 'Stalked',   kills: 1000,  bountyBonus: 0.18, dropRate: 0.02,  accuracy: 3, defIgnore: 0 },
    { rank: 6, title: 'Claimed',   kills: 2500,  bountyBonus: 0.22, dropRate: 0.025, accuracy: 4, defIgnore: 1 },
    { rank: 7, title: 'Conquered', kills: 5000,  bountyBonus: 0.26, dropRate: 0.035, accuracy: 5, defIgnore: 2 },
    { rank: 8, title: 'Nemesis',   kills: 10000, bountyBonus: 0.30, dropRate: 0.05,  accuracy: 6, defIgnore: 3 },
];

/**
 * Get the bounty mark data for a mob based on lifetime kills
 * @param {string} mobId - Mob identifier
 * @returns {Object} Mark data with rank, title, killCount, and all bonuses
 */
export function getBountyMark(mobId) {
    const killCount = gameState.getKillCount(mobId);
    let mark = BOUNTY_MARKS[0];
    for (let i = BOUNTY_MARKS.length - 1; i >= 0; i--) {
        if (killCount >= BOUNTY_MARKS[i].kills) {
            mark = BOUNTY_MARKS[i];
            break;
        }
    }
    return { ...mark, killCount };
}

export default {
    PLAYER_CONTRACTS,
    CONTRACT_TIERS,
    BOUNTY_CONFIG,
    BOUNTY_MARKS,
    getBountyXP,
    getBountyMark
};
